import React, { createContext, useCallback, useContext, useState } from 'react';

/* ─── public config shape ─────────────────────────────────────────────── */
export type AddPaymentReceiptCategory = 'customer' | 'gst' | 'advocate' | 'other';
export type AddPaymentLegalChargeType =
  | ''
  | 'stamp_duty'
  | 'registration_charges'
  | 'agreement_fee'
  | 'deed_fee';

export type AddPaymentConfig = {
  /** Pre-select a unit (fills unit picker and unit number) */
  unitId?: string;
  unitNumber?: string;
  /** Pre-select a booking (used for bookingId on the saved txn) */
  bookingId?: string;
  /** Pre-select the receipt being refunded */
  refundAgainstTxnId?: string;
  /** Pre-select payment category */
  receiptCategory?: AddPaymentReceiptCategory;
  /** Pre-fill amount (e.g. GST due) */
  amount?: number;
  /** Pre-fill customer / party name */
  partyName?: string;
  /** Pre-select a bank account */
  bankAccountId?: string;
  /** Edit an existing transaction by ID */
  editTxnId?: string;
  /** Pre-set direction (default: 'receipt') */
  defaultTxnType?: 'receipt' | 'payment';
  /** Pre-set outgoing payment category (default: 'vendor_payment') */
  defaultPaymentCategory?: 'customer_refund' | 'vendor_payment';
  /** Called with the saved/updated Firestore doc ID */
  onSaved?: (txnId: string) => void;
};

/* ─── context ─────────────────────────────────────────────────────────── */
type AddPaymentContextType = {
  openAddPayment: (config?: AddPaymentConfig) => void;
};

const AddPaymentContext = createContext<AddPaymentContextType>({
  openAddPayment: () => {},
});

/* ─── provider ────────────────────────────────────────────────────────── */
// Lazy import so the heavy drawer bundle doesn't block initial render.
const AddPaymentDrawer = React.lazy(
  () => import('../components/AddPaymentDrawer'),
);

export function AddPaymentProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<AddPaymentConfig | null>(null);

  const openAddPayment = useCallback((cfg: AddPaymentConfig = {}) => {
    setConfig(cfg);
  }, []);

  const handleClose = useCallback(() => {
    setConfig(null);
  }, []);

  const handleSaved = useCallback(
    (txnId: string) => {
      config?.onSaved?.(txnId);
      setConfig(null);
    },
    [config],
  );
  // "Add another after saving": tell the caller, but keep the drawer open for the next entry.
  const handleSavedKeepOpen = useCallback(
    (txnId: string) => {
      config?.onSaved?.(txnId);
    },
    [config],
  );

  return (
    <AddPaymentContext.Provider value={{ openAddPayment }}>
      {children}
      {/* Suspense boundary keeps the lazy chunk from blocking anything */}
      <React.Suspense fallback={null}>
        <AddPaymentDrawer
          open={config !== null}
          config={config}
          onClose={handleClose}
          onSaved={handleSaved}
          onSavedKeepOpen={handleSavedKeepOpen}
        />
      </React.Suspense>
    </AddPaymentContext.Provider>
  );
}

/* ─── hook ────────────────────────────────────────────────────────────── */
export function useAddPayment(): AddPaymentContextType {
  return useContext(AddPaymentContext);
}
